import { reactive, ref, Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Filters, Paginated } from '@/core/services/payment/types'
import { handleApiError } from '@/core/helpers/handleApiError'

interface Options {
  /**
   * Все поля фильтра с «пустыми» значениями по умолчанию.
   * Тип значения задаёт разбор из URL: [] = массив, false = флаг, 0 = число, остальное = строка.
   */
  defaults: Filters
  perPage?: number
  /** Сообщение, если запрос списка не удался. */
  errorText?: string
}

const toUrlValue = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    return value.length ? value.join(',') : null
  }
  if (value === true) {
    return 'true'
  }
  if (value === false || value === null || value === undefined || value === '') {
    return null
  }
  return String(value)
}

const fromUrlValue = (raw: string, sample: unknown): unknown => {
  if (Array.isArray(sample)) {
    return raw ? raw.split(',') : []
  }
  if (typeof sample === 'boolean') {
    return raw === 'true' || raw === '1'
  }
  if (typeof sample === 'number') {
    return Number(raw)
  }
  return raw
}

/**
 * Список с фильтрами и пагинацией: состояние, загрузка, синхронизация с адресной строкой.
 * Фильтры хранятся в URL, поэтому страницу можно обновить или отправить ссылкой.
 * Устаревшие ответы (если фильтр сменили во время загрузки) отбрасываются.
 */
export function usePaginatedList<T>(
  fetcher: (params: Filters) => Promise<Paginated<T>>,
  options: Options
) {
  const route = useRoute()
  const router = useRouter()
  const perPage = options.perPage || 20

  const filters = reactive<Filters>({ ...options.defaults })
  const items = ref([]) as Ref<T[]>
  const page = ref(1)
  const pages = ref(1)
  const total = ref(0)
  const loading = ref(false)
  let requestId = 0

  /** Заполняет фильтры значениями из URL. */
  const restoreFromUrl = () => {
    Object.keys(options.defaults).forEach(key => {
      const raw = route.query[key]
      if (typeof raw === 'string') {
        filters[key] = fromUrlValue(raw, options.defaults[key])
      }
    })
    const urlPage = Number(route.query.page)
    page.value = urlPage > 0 ? urlPage : 1
  }

  const writeToUrl = () => {
    const query: Record<string, string> = {}
    Object.keys(filters).forEach(key => {
      const value = toUrlValue(filters[key])
      if (value !== null) {
        query[key] = value
      }
    })
    if (page.value > 1) {
      query.page = String(page.value)
    }
    router.replace({ query })
  }

  const load = async () => {
    const current = ++requestId
    loading.value = true
    try {
      const result = await fetcher({
        ...filters,
        page: page.value,
        per_page: perPage,
      })
      if (current !== requestId) {
        return
      }
      items.value = result.items
      pages.value = result.pages
      total.value = result.total
    } catch (error) {
      if (current === requestId) {
        items.value = []
        total.value = 0
        pages.value = 1
        handleApiError(error, options.errorText || 'Не удалось загрузить список')
      }
    } finally {
      if (current === requestId) {
        loading.value = false
      }
    }
  }

  /** Применить фильтр: с первой страницы. */
  const apply = () => {
    page.value = 1
    writeToUrl()
    return load()
  }

  const reset = () => {
    Object.keys(options.defaults).forEach(key => {
      filters[key] = Array.isArray(options.defaults[key])
        ? [...(options.defaults[key] as unknown[])]
        : options.defaults[key]
    })
    return apply()
  }

  const setPage = (value: number) => {
    page.value = value
    writeToUrl()
    return load()
  }

  /** Первая загрузка: сначала фильтры из URL. */
  const init = () => {
    restoreFromUrl()
    return load()
  }

  return {
    filters,
    items,
    page,
    pages,
    perPage,
    total,
    loading,
    init,
    load,
    apply,
    reset,
    setPage,
  }
}
